'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Loader2, Plus, Pencil, Trash2, Save } from 'lucide-react'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { formatCredits, parseCreditsToMicroUsd, creditsInputValue } from '@/lib/billing/utils/credits'
import {
  BILLING_CHANNELS,
  DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS,
  DEFAULT_TOPUP_CREDIT_LIFETIME_MONTHS,
} from '@/lib/billing/constants'

// =============================================================================
// Types
// =============================================================================

interface BillingSettingsData {
  pool_trial_reserve_percent: number
  /** Global kill-switch for tenant-initiated plan change (Phase 3.3 route). */
  allow_self_serve_plan_change: boolean
  /** Ceiling on what one tenant may top up per Shopify billing cycle, in cents. */
  shopify_topup_cycle_cap_cents: number
  /** Months top-up credits last; null = never. */
  topup_credit_lifetime_months: number | null
}

interface TopupPackage {
  id: string
  name: string
  amount_microusd: number
  price_cents: number
  stripe_price_id: string | null
  billing_channel: string
  is_best_value: boolean
  is_active: boolean
  sort_order: number
}


// =============================================================================
// SettingsTab
// =============================================================================

export function SettingsTab() {
  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const canManage = can('billing.manage_plans')

  const [_settings, setSettings] = useState<BillingSettingsData | null>(null)
  const [packages, setPackages] = useState<TopupPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Settings form
  const [poolReserve, setPoolReserve] = useState(20)
  const [allowSelfServePlanChange, setAllowSelfServePlanChange] = useState(true)
  const [shopifyTopupCapCents, setShopifyTopupCapCents] = useState(DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)
  // Empty = never.
  const [topupLifetimeMonths, setTopupLifetimeMonths] = useState(String(DEFAULT_TOPUP_CREDIT_LIFETIME_MONTHS))
  const [savingSettings, setSavingSettings] = useState(false)

  // Package dialog
  const [pkgDialogOpen, setPkgDialogOpen] = useState(false)
  const [editingPkg, setEditingPkg] = useState<TopupPackage | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [settingsRes, packagesRes] = await Promise.all([
        fetchWithAuth('/api/admin/billing/settings'),
        fetchWithAuth('/api/admin/billing/topup-packages'),
      ])

      if (!settingsRes.ok || !packagesRes.ok) {
        throw new Error('Failed to fetch settings data')
      }

      const settingsData = await settingsRes.json()
      const packagesData = await packagesRes.json()

      const s = settingsData.settings as BillingSettingsData | null
      setSettings(s)
      if (s) {
        setPoolReserve(s.pool_trial_reserve_percent)
        // Schema default is TRUE; treat missing/null as enabled for pre-migration rows.
        setAllowSelfServePlanChange(s.allow_self_serve_plan_change ?? true)
        setShopifyTopupCapCents(s.shopify_topup_cycle_cap_cents ?? DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)
        setTopupLifetimeMonths(s.topup_credit_lifetime_months === null ? '' : String(s.topup_credit_lifetime_months))
      }
      setPackages(packagesData.packages ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => { fetchData() }, [fetchData])

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true)
      const res = await fetchWithAuth('/api/admin/billing/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pool_trial_reserve_percent: poolReserve,
          allow_self_serve_plan_change: allowSelfServePlanChange,
          shopify_topup_cycle_cap_cents: shopifyTopupCapCents,
          topup_credit_lifetime_months: topupLifetimeMonths.trim() === '' ? null : Number(topupLifetimeMonths),
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to save settings')
      }
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleDeletePkg = async (pkg: TopupPackage) => {
    if (!confirm(`Deactivate "${pkg.name}"?`)) return
    try {
      const res = await fetchWithAuth(`/api/admin/billing/topup-packages/${pkg.id}`, { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json()
        alert(data.error || 'Failed to deactivate')
        return
      }
      fetchData()
    } catch {
      alert('Failed to deactivate package')
    }
  }

  const handleSavePkg = async (data: Record<string, unknown>) => {
    const url = editingPkg
      ? `/api/admin/billing/topup-packages/${editingPkg.id}`
      : '/api/admin/billing/topup-packages'
    const method = editingPkg ? 'PATCH' : 'POST'

    const res = await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to save package')
    }

    setPkgDialogOpen(false)
    fetchData()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return <div className="text-center text-destructive py-12">{error}</div>
  }

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Global Settings</CardTitle>
          <CardDescription>
            Pool reserve and top-up credit lifetime. The default low-balance threshold is set on the Low
            balance notification under System Notifications → Tenant notifications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Pool Trial Reserve %</Label>
              <Input
                type="number" min={0} max={100}
                value={poolReserve}
                onChange={(e) => setPoolReserve(parseInt(e.target.value) || 0)}
                disabled={!canManage}
              />
            </div>
            <div>
              <Label htmlFor="topup-credit-lifetime">Top-up credits expire after (months)</Label>
              <Input
                id="topup-credit-lifetime"
                type="number" min={1} max={120} step={1}
                placeholder="Never"
                value={topupLifetimeMonths}
                onChange={(e) => setTopupLifetimeMonths(e.target.value)}
                disabled={!canManage}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Counted from each top-up, on both channels; top-ups already credited keep their date. Empty = never.
              </p>
            </div>
          </div>
          <div className="flex items-start justify-between rounded-md border p-3 gap-4">
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="self-serve-plan-change">Allow tenants to change plan</Label>
              <p className="text-xs text-muted-foreground">
                <span>When off, the tenant billing page hides the Change Plan block and</span>
                <code className="mx-1">POST /api/billing/subscription/change-plan</code>
                <span>returns 403</span> <code>plan_change_disabled</code><span>. Per-tenant overrides
                (Force allow / Force deny) on the Tenants tab take precedence.</span>
              </p>
            </div>
            <Switch
              id="self-serve-plan-change"
              checked={allowSelfServePlanChange}
              onCheckedChange={setAllowSelfServePlanChange}
              disabled={!canManage}
            />
          </div>
          {canManage && (
            <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              <span>Save Settings</span>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Stripe top-up packages */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">Top-up packages — Stripe</CardTitle>
              <CardDescription>
                The amounts Stripe-billed tenants can buy. Shopify tenants never see these — their
                amounts come from their own plan, configured below.
              </CardDescription>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setEditingPkg(null); setPkgDialogOpen(true) }}>
                <Plus className="h-4 w-4 mr-1" /> <span>Add Package</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="w-[100px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} className={!pkg.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">
                    {pkg.name}
                    {pkg.is_best_value && <Badge variant="default" className="ml-2">Best Value</Badge>}
                  </TableCell>
                  <TableCell>{formatCredits(pkg.amount_microusd)}</TableCell>
                  <TableCell><span>$</span>{(pkg.price_cents / 100).toFixed(2)}</TableCell>
                  <TableCell><Badge variant="outline">{pkg.billing_channel}</Badge></TableCell>
                  <TableCell>
                    {pkg.is_active
                      ? <Badge variant="default">Active</Badge>
                      : <Badge variant="outline">Inactive</Badge>}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditingPkg(pkg); setPkgDialogOpen(true) }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {pkg.is_active && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeletePkg(pkg)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 6 : 5} className="text-center text-muted-foreground py-8">
                    No topup packages configured
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Shopify top-ups — read-only explanation plus the one thing we do control */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Shopify top-ups</CardTitle>
          <CardDescription>
            Read from each merchant&apos;s own App Pricing plan — there is nothing to author here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>
              <span>Amounts are the</span> <code>topup-</code> <span>usage meters on the plan, authored in the Partner
              Dashboard. Shopify allows at most five meters per plan.</span>
            </li>
            <li>Shopify owns the price; the merchant is billed on their next Shopify invoice.</li>
            <li>
              Credit is the price 1:1 plus the plan&apos;s top-up bonus percent. No custom amounts —
              every meter must exist on the plan beforehand.
            </li>
          </ul>
          <div className="max-w-xs">
            <Label>Exposure cap per billing cycle ($)</Label>
            <Input
              type="number" min={0} step="1"
              value={(shopifyTopupCapCents / 100).toFixed(2)}
              onChange={(e) =>
                setShopifyTopupCapCents(Math.round((parseFloat(e.target.value) || 0) * 100))
              }
              disabled={!canManage}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              <span>Shopify usage is postpaid and offers no cap of its own, so this is the only limit on
              how much unsecured credit one tenant can take before their invoice settles. Default</span>{' '}
              {formatCentsAsUsd(DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)}<span>. A single tenant can be given
              a different limit, but only in the database — there is no per-tenant control yet.</span>
            </p>
          </div>
          {canManage && (
            <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              <span>Save Settings</span>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Package Dialog */}
      <TopupPackageDialog
        open={pkgDialogOpen}
        onOpenChange={setPkgDialogOpen}
        pkg={editingPkg}
        onSave={handleSavePkg}
      />
    </div>
  )
}

// =============================================================================
// TopupPackageDialog
// =============================================================================

function TopupPackageDialog({
  open, onOpenChange, pkg, onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pkg: TopupPackage | null
  onSave: (data: Record<string, unknown>) => Promise<void>
}) {
  const isEdit = !!pkg
  const [name, setName] = useState('')
  const [amountCredits, setAmountCredits] = useState('')
  const [priceUsd, setPriceUsd] = useState('')
  const [isBestValue, setIsBestValue] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      if (pkg) {
        setName(pkg.name)
        setAmountCredits(creditsInputValue(pkg.amount_microusd))
        setPriceUsd((pkg.price_cents / 100).toString())
        setIsBestValue(pkg.is_best_value)
        setIsActive(pkg.is_active)
        setSortOrder(pkg.sort_order)
      } else {
        setName('')
        setAmountCredits('')
        setPriceUsd('')
        setIsBestValue(false)
        setIsActive(true)
        setSortOrder(0)
      }
      setError(null)
    }
  }, [open, pkg])

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    const amountMicro = parseCreditsToMicroUsd(amountCredits)
    const priceCents = Math.round(parseFloat(priceUsd) * 100)
    if (amountMicro === null || amountMicro <= 0) { setError('Amount must be positive'); return }
    if (isNaN(priceCents) || priceCents <= 0) { setError('Price must be positive'); return }

    try {
      setSaving(true)
      setError(null)
      await onSave({
        name: name.trim(),
        amount_microusd: amountMicro,
        price_cents: priceCents,
        // Only on create: a legacy Shopify row keeps its channel instead of being
        // silently converted, and the route refuses to mint a new one either way.
        ...(isEdit ? {} : { billing_channel: BILLING_CHANNELS.STRIPE }),
        is_best_value: isBestValue,
        is_active: isActive,
        sort_order: sortOrder,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Package' : 'Add Package'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="2,500 credits" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Balance Amount (credits)</Label>
              <Input value={amountCredits} onChange={(e) => setAmountCredits(e.target.value)} placeholder="2500" />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)} placeholder="25" />
            </div>
          </div>
          <div>
            <Label>Sort Order</Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)} />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={isBestValue} onCheckedChange={setIsBestValue} />
              <Label>Best Value</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>Active</Label>
            </div>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <span>{isEdit ? 'Save' : 'Create'}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
